/**
 * Page ↔ shell contract for the redesigned pages (SOC-792).
 *
 * The prototypes scope page CSS on <body> (`body[data-nav="content"]`, `body.performance`,
 * `body.launcher-page`) and put the breadcrumb in the shared topbar. A page declares both once
 * with `usePageShell` and the shell renders them; on unmount everything is cleared, so a legacy
 * page that follows never inherits a redesigned page's body class.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export type Crumb = { label: string; to?: string };

type ShellState = {
  crumbs: Crumb[];
  setCrumbs: (c: Crumb[]) => void;
};

const ShellContext = createContext<ShellState | null>(null);

export function ShellProvider({ children }: { children: ReactNode }) {
  const [crumbs, setCrumbs] = useState<Crumb[]>([]);
  const value = useMemo(() => ({ crumbs, setCrumbs }), [crumbs]);
  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
}

export function useShell(): ShellState {
  const ctx = useContext(ShellContext);
  if (!ctx) throw new Error("useShell must be used within <ShellProvider>");
  return ctx;
}

type PageShellOptions = {
  /** `data-nav` on <body>, the prototype's own hook for page-scoped CSS and the active nav item. */
  nav: string;
  /** Extra class(es) on <body> (`performance`, `launcher-page`, `launch-status`). */
  bodyClass?: string;
  crumbs: Crumb[];
};

export function usePageShell({ nav, bodyClass, crumbs }: PageShellOptions) {
  const { setCrumbs } = useShell();
  const crumbsKey = JSON.stringify(crumbs);

  useEffect(() => {
    const body = document.body;
    const classes = (bodyClass ?? "").split(/\s+/).filter(Boolean);
    body.setAttribute("data-nav", nav);
    classes.forEach(c => body.classList.add(c));
    return () => {
      body.removeAttribute("data-nav");
      classes.forEach(c => body.classList.remove(c));
    };
  }, [nav, bodyClass]);

  useEffect(() => {
    setCrumbs(crumbs);
    return () => setCrumbs([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crumbsKey, setCrumbs]);
}
